import type { ReactNode } from 'react'
import { ArrowRight } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { digits } from '@/ui/digits'

// 사양을 바꾸는 세 창 — 소유자의 변경 신청과 최고 관리자의 사양 수정(`frontend/src/views/user/ChangeDialog.tsx`),
// 변경 승인(`frontend/src/views/vm/ChangeApproveDialog.tsx`) — 의 본문 조각이다. 자원마다 카드 하나이고, 머리 오른쪽이
// 그 VM 의 노드에서 가질 수 있는 끝(`최대 n코어`), 본문이 `지금 → 바꿀 값` 이다. 맨 아래 `바뀌는 것` 이 보내면 무엇이 바뀌는지 줄마다 말한다.
// 세 창이 같은 조각을 써서 같은 VM 이 창마다 다른 모양으로 서지 않는다.

/** 자원 하나의 카드. `aside` 는 머리 오른쪽 — 그 VM 의 노드에서 가질 수 있는 끝이다. */
export function SpecCard({ title, aside, children }: { title: string; aside?: string | undefined; children: ReactNode }) {
  return (
    <section aria-label={title} className="grid gap-2 rounded-lg border bg-card px-3 py-2.5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <h3 className="text-[13px] font-semibold">{title}</h3>
        {aside ? <span className="text-[12px] text-muted-foreground tabular-nums">{aside}</span> : null}
      </div>
      {children}
    </section>
  )
}

/**
 * `지금 4코어 → [ 8 ] 코어`. 비운 칸은 지금 값이 그대로 남는다 — placeholder 가 그 값을 말한다.
 * 칸은 `components/NumberField.tsx` 와 같은 걸러개라 잘못 친 글자는 칸에 들어오지 않는다.
 */
export function NowTo({
  id,
  label,
  now,
  unit,
  value,
  placeholder,
  busy,
  bad = false,
  onChange,
}: {
  id: string
  label: string
  now: string
  unit: string
  value: string
  placeholder: string
  busy: boolean
  bad?: boolean
  onChange: (v: string) => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <span className="text-[13px] text-muted-foreground tabular-nums">지금 {now}</span>
      <ArrowRight className="size-3.5 flex-none text-muted-foreground" aria-hidden />
      <span className="flex items-center gap-1.5">
        <Input
          id={id}
          aria-label={label}
          value={value}
          inputMode="numeric"
          placeholder={placeholder}
          autoComplete="off"
          disabled={busy}
          aria-invalid={bad || undefined}
          className="w-24 tabular-nums"
          onChange={(e) => {
            const next = digits(e.target.value)
            if (next !== null) onChange(next)
          }}
        />
        <span className="text-[13px] text-muted-foreground">{unit}</span>
      </span>
    </div>
  )
}

/** 칸 아래 한 줄 — 받는 범위를 말하고, 넘으면 같은 자리가 막힘이 된다. */
export function Note({ bad = false, children }: { bad?: boolean; children: ReactNode }) {
  return <p className={cn('text-[11px]', bad ? 'text-danger-ink' : 'text-muted-foreground')}>{children}</p>
}

/** 보내면 바뀌는 것, 한 줄에 하나. 없으면 무엇을 해야 하는지 한 문장이다. */
export function ChangeLines({ lines }: { lines: string[] }) {
  return (
    <div className="rounded-lg border bg-surface-2/60 px-3 py-2">
      <div className="mb-1 text-[10px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">바뀌는 것</div>
      {lines.length === 0 ? (
        <p className="text-[12px] text-muted-foreground">바꿀 값을 적으세요.</p>
      ) : (
        <ul aria-label="바뀌는 것" className="grid gap-0.5 text-[13px] tabular-nums">
          {lines.map((l) => (
            <li key={l}>{l}</li>
          ))}
        </ul>
      )}
    </div>
  )
}
